import type { Phrase } from '../types';

/** Strings for the step-by-step booking flow. Every key starts with `bkf`. */
export const bookingEn = {
  // page chrome
  bkfTitle: 'Request to book',
  bkfSubtitle: 'A few short steps. Nothing to pay now.',
  bkfBackToVenue: 'Back to venue',
  bkfDraftRestored: 'We kept the details you entered earlier.',
  bkfStartOver: 'Start over',

  // progress
  bkfProgress: 'Booking progress',
  bkfStepOf: 'Step {n} of {total}: {name}',
  bkfStepDone: 'completed',
  bkfStepEvent: 'Event',
  bkfStepDate: 'Date & time',
  bkfStepGuests: 'Guests',
  bkfStepExtras: 'Requirements',
  bkfStepReview: 'Review',
  bkfStepSend: 'Send',
  bkfNext: 'Continue',
  bkfBack: 'Back',

  // step 1
  bkfEventTitle: 'What are you planning?',
  bkfEventBody: 'Choose the kind of event. The venue sees this with your request.',
  bkfSuggested: 'Suggested',
  bkfSuggestionNote: 'Suggested events are the ones this kind of venue is often booked for. You can pick any event.',
  bkfEventRequired: 'Choose the kind of event to continue.',

  // step 2
  bkfDateTitle: 'When do you need the venue?',
  bkfDateBody: 'Pick a day, then a session, hours or several days.',
  bkfYourSlot: 'Your chosen time',
  bkfSlotPrefilled: 'Chosen on the venue page. Pick another time below to change it.',
  bkfSlotRequired: 'Choose a date and time to continue.',
  bkfQuoteLoading: 'Working out the price…',
  bkfQuoteProblem: 'This time cannot be booked',

  // step 3
  bkfGuestsTitle: 'How many guests?',
  bkfGuestsBody: 'An estimate is fine. It helps the venue plan seating.',
  bkfGuestsLabel: 'Expected guests',
  bkfSeats: 'Seats {count}',
  bkfFloatingCap: '+ {count} standing / floating',
  bkfMaxCap: 'Up to {count} guests in all',
  bkfGuestsRequired: 'Enter the number of guests (1 or more).',
  bkfGuestsWhole: 'Enter a whole number.',
  bkfGuestsTooMany: 'This venue holds up to {count} guests.',
  bkfGuestsStanding: 'More than the seated capacity, so some guests will be standing.',
  bkfLess: 'Fewer guests',
  bkfMore: 'More guests',

  // step 4
  bkfExtrasTitle: 'Anything else the venue should know?',
  bkfExtrasBody: 'These are requests to the venue, not guarantees. The venue will tell you what they can arrange.',
  bkfExtrasGroup: 'Requests for the venue',
  bkfReqStage: 'Stage decoration',
  bkfReqSound: 'Sound & lights',
  bkfReqCatering: 'Catering',
  bkfReqSeating: 'Extra seating',
  bkfReqParking: 'Parking for guests',
  bkfReqAc: 'AC throughout',
  bkfReqPhoto: 'Space for photography',
  bkfNotesLabel: 'Notes for the venue',
  bkfOptional: 'optional',
  bkfNotesPlaceholder: 'e.g. programme timings, number of cars, anything the venue should prepare',
  bkfNotesCount: '{count} / {max}',
  bkfNotesTooLong: 'Your notes and requests together must be under {max} characters. Shorten your notes.',

  // step 5
  bkfReviewTitle: 'Check your request',
  bkfReviewBody: 'Tap Edit on any row to change it before you send.',
  bkfEdit: 'Edit',
  bkfEditLabel: 'Edit {section}',
  bkfEvent: 'Event',
  bkfWhen: 'When',
  bkfGuests: 'Guests',
  bkfGuestCount: { one: '{count} guest', other: '{count} guests' },
  bkfRequests: 'Requests',
  bkfNotes: 'Notes',
  bkfNone: 'None',
  bkfEstimate: 'Estimated total',
  bkfNoPayNow: 'No payment now. The venue confirms your request, then you pay them directly.',
  bkfTermsTitle: 'Venue terms',
  bkfTermBuffer: { one: 'The venue keeps {count} minute free before and after each event for cleaning.', other: 'The venue keeps {count} minutes free before and after each event for cleaning.' },
  bkfTermPeak: 'Saturdays and Sundays cost {percent}% more than weekdays. The estimate already includes this.',
  bkfTermPayAtVenue: 'No advance payment. Once the venue confirms, you pay the full amount at the venue.',

  // step 6
  bkfSendTitle: 'Send your request',
  bkfSendBody: '{venue} gets your request as soon as you send it. This is what happens next:',
  bkfAck: 'I understand this is a request: the venue confirms it, and I pay the venue directly.',
  bkfAckRequired: 'Tick the box to confirm you understand.',
  bkfSend: 'Send request',
  bkfSending: 'Sending…',

  // result
  bkfDoneTitle: 'Your request is with the venue',
  bkfDoneBody: 'Request #{id} has been sent to {venue}.',
  bkfDoneDuplicate: 'You had already asked for this time. This is the same request #{id}; nothing new was sent.',
  bkfMoreVenues: 'Explore more venues',
  bkfConflictTitle: 'That time is no longer available',
  bkfErrorTitle: 'We could not send your request',
  bkfPickAnother: 'Choose another time',
  bkfTryAgain: 'Try again',

  // summary card
  bkfSummary: 'Your booking',
  bkfNoSlot: 'No time chosen yet',

  // manager views
  bkfNotesFromGuest: 'Notes',

  // slot picker: modes, prices, several days, half day, cleaning buffer
  bkfByHalfDay: 'Half day',
  bkfRatePerHour: '{price} per hour.',
  bkfRatePerHalfDay: '{price} per half day.',
  bkfRatePerDay: '{price} per day.',
  bkfRateSessionHalf: 'Each session {price}',
  bkfRateSessionHour: 'Sessions are charged {price} per hour',
  bkfRateFullDay: 'Full day {price}',
  bkfRateWeekendNote: 'Saturdays and Sundays cost {percent}% more.',
  bkfNoPricesTitle: 'This venue has not set its prices yet',
  bkfNoPricesBody: 'It cannot be booked online until it does. Please call or message the venue to ask about a date.',
  bkfDaysTooMany: 'One booking can cover up to {max} days.',
  bkfDaysCrossTaken: 'Some days in that range are not available. Pick a range without greyed-out days.',
  bkfDaysPickFirst: 'Tap the first day of your event.',
  bkfDaysPickLast: 'Now tap the last day (up to {max} days). For just one day, tap the same day again.',
  bkfDaysSingle: 'One full day. Tap another day to make it longer.',
  bkfBufferTip: { one: 'Kept free for cleaning: the venue needs {count} minute before and after every booking.', other: 'Kept free for cleaning: the venue needs {count} minutes before and after every booking.' },
  bkfBufferExplain: { one: '"Cleaning time" means the slot is right next to another booking. The venue keeps {count} minute free before and after every event to clean and set up, so those slots cannot be booked.', other: '"Cleaning time" means the slot is right next to another booking. The venue keeps {count} minutes free before and after every event to clean and set up, so those slots cannot be booked.' },
  bkfDayInBuffer: 'Not available. It is too close to another booking (cleaning time).',
  bkfHalfDayHeading: 'Pick a half day',
  bkfHalfFirst: 'First half',
  bkfHalfSecond: 'Second half',
  bkfPickHalfDay: 'Pick the first or the second half of the day to see the price.',

  // how a quote adds up
  bkfQuoteBreakdown: 'How the total is worked out',
  bkfLineHours: { one: '{count} hour', other: '{count} hours' },
  bkfLineHalfDays: { one: '{count} session', other: '{count} sessions' },
  bkfLineDays: { one: '{count} day', other: '{count} days' },
  bkfLineWeekend: 'weekend rate',

  // refunds on cancelled bookings
  bkfRefundPending: 'Refund of {amount} due from the venue',
  bkfRefundDone: '{amount} refunded',
  bkfRefundNoneTier: 'Cancelled too close to the event for a refund',
  bkfCxlYouPaid: 'You have paid the venue',
  bkfCxlRefundAmount: 'You will get back',
  bkfCxlDone: 'Booking cancelled.',
  bkfMarkRefunded: 'Mark refunded',
  bkfMarkRefundedTip: 'Press this once you have handed the refund back to the guest. They will see it as refunded.',
  bkfRefundMarked: 'Refund marked as returned.',

  // manager: money taken with a counter booking
  bkfAmountReceivedNow: 'Amount received now (₹)',
  bkfAmountReceivedHint: 'Enter any part of the total, or the full amount. The rest stays as the balance due.',
  bkfAmountOverTotal: 'That is more than the total ({amount}).',
  bkfBalanceAfter: 'Balance due',
  bkfReceivedNow: 'Received now',

  // manager: what the approve / reject buttons do
  bkfApproveTip: 'Confirm this booking. The date is taken off sale and the guest is told it is confirmed.',
  bkfRejectTip: 'Decline this request. The guest is told the venue cannot take it, and the date stays open for others.',
  bkfWhenRange: '{from} to {to}',
} satisfies Record<string, Phrase>;
